/*
 * Formulario de fin de jornada.
 * Muestra tiempo transcurrido en vivo, calcula horas totales y actualiza el worklog.
 */
'use client'
import React, { useEffect, useState } from 'react'
import { Form, Button, Space, Input, Row, Col, Typography, notification, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import {
  WorklogUpdateInput,
  WorklogWhereUniqueInput
} from '../../../generated/graphql'
import { UPDATE_WORKLOG } from '../gql/verteOfficeGQL'
import { useMutation } from '@apollo/client'
import { worklogBadge } from '../../WorklogStatusBadge/WorklogStatusBadge'

dayjs.extend(duration)

const { Text } = Typography

type updateWorklogInputType = {
  data: WorklogUpdateInput
  where: WorklogWhereUniqueInput
}

interface EndTimeRecordFormProps {
  worklogId: string
  startTime: string | Date
  onCancel: () => void
  onClose: () => void
}

export const EndTimeRecordForm: React.FC<EndTimeRecordFormProps> = ({
  worklogId,
  startTime,
  onCancel,
  onClose
}) => {
  const [updateWorklog, { loading }] = useMutation<any, updateWorklogInputType>(
    UPDATE_WORKLOG,
    {
      fetchPolicy: 'no-cache'
    }
  )
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [elapsedTime, setElapsedTime] = useState('')
  const [elapsedMinutes, setElapsedMinutes] = useState(0)
  const isUnderMinimum = elapsedMinutes < 10

  useEffect(() => {
    const calculateElapsedTime = () => {
      const start = dayjs(startTime)
      const now = dayjs()
      const diff = now.diff(start)

      const dur = dayjs.duration(diff)
      const hours = Math.floor(dur.asHours())
      const minutes = dur.minutes()
      const seconds = dur.seconds()

      setElapsedTime(`${hours}h ${minutes}m ${seconds}s`)
      setElapsedMinutes(Math.floor(dur.asMinutes()))
    }

    calculateElapsedTime()
    const interval = setInterval(calculateElapsedTime, 1000)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const calculateTotalHours = () => {
    const start = dayjs(startTime)
    const end = dayjs()
    const diffInMinutes = end.diff(start, 'minute')
    const totalHours = diffInMinutes / 60
    return parseFloat(totalHours.toFixed(2)) // Redondea a 2 decimales
  }

  const handleSubmit = async (values: any) => {
    try {
      await updateWorklog({
        variables: {
          data: {
            status: 'COMPLETE',
            end_time: new Date().toISOString(),
            total_hours: calculateTotalHours(),
            observation: values.observations
          },
          where: {
            id: worklogId
          }
        }
      })

      notification.success({
        message: t('worklog_ended_successfully')
      })
      worklogBadge.refresh()
      onClose()
    } catch (error: any) {
      notification.error({
        message: t('error_ending_worklog'),
        description: error.message
      })
    } finally {
      form.resetFields()
    }
  }

  return (
    <Form form={form} layout='vertical' onFinish={handleSubmit}>
      <Row gutter={[16, 8]}>
        <Col span={24}>
          <div
            style={{
              padding: '12px',
              background: '#f0f2f5',
              borderRadius: '6px',
              marginBottom: '16px',
              textAlign: 'center'
            }}
          >
            <Text strong style={{ fontSize: '16px' }}>
              {t('elapsed_time')}: {elapsedTime}
            </Text>
          </div>
        </Col>

        <Col span={24}>
          <Form.Item
            name='observations'
            label={t('observations')}
            rules={[{ required: true, message: t('field_required') }]}
            style={{ marginBottom: 8 }}
          >
            <Input.TextArea
              size='small'
              rows={4}
              style={{ width: '100%' }}
              placeholder={t('observations')}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 0
            }}
          >
            <Space>
              <Button onClick={onCancel} size='small' shape='round' danger>
                {t('cancel')}
              </Button>
              <Tooltip title={isUnderMinimum ? 'Deben pasar por lo menos 10 minutos para finalizar' : ''}>
                <span>
                  <Button
                    type='primary'
                    htmlType='submit'
                    shape='round'
                    size='small'
                    loading={loading}
                    disabled={isUnderMinimum}
                  >
                    {t('finish')}
                  </Button>
                </span>
              </Tooltip>
            </Space>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  )
}

export default EndTimeRecordForm
